import type {
    ContentDefinition,
    ForDefinition,
    ImportCachedSymbol,
    PluginDefinition,
    TemplateInputSchema,
    UseBinding,
} from "@magic-md/core";
import {
    buildDefaultedTemplateInputs,
    getRequiredTemplateInputs,
} from "@magic-md/core";

export type TemplateSourceSummary = {
    params?: string[];
    inputSchema?: TemplateInputSchema;
};

export function resolveUseTemplateSource(
    source: string,
    definitions: Map<string, PluginDefinition>,
    contentDefinitions?: Map<string, ContentDefinition>,
    forDefinitions?: Map<string, ForDefinition>,
    uses?: readonly UseBinding[],
    importSymbols?: readonly ImportCachedSymbol[],
): TemplateSourceSummary | undefined {
    const localDefinition = definitions.get(source);
    if (localDefinition?.template && (localDefinition.params?.length || localDefinition.inputSchema)) {
        return { params: localDefinition.params, inputSchema: localDefinition.inputSchema };
    }

    const localContent = contentDefinitions?.get(source);
    if (localContent?.template && (localContent.params?.length || localContent.inputSchema)) {
        return { params: localContent.params, inputSchema: localContent.inputSchema };
    }

    const localFor = forDefinitions?.get(source);
    if (localFor?.template && (localFor.params?.length || localFor.inputSchema)) {
        return { params: localFor.params, inputSchema: localFor.inputSchema };
    }

    const localUse = uses?.find((useBinding) => useBinding.id === source);
    if (localUse?.template && (localUse.params?.length || localUse.inputSchema)) {
        return { params: localUse.params, inputSchema: localUse.inputSchema };
    }

    const matchedSymbol = importSymbols?.find(
        (symbol) => symbol.qualifiedId === source || symbol.publicName === source,
    );
    if (matchedSymbol?.template && (matchedSymbol.params?.length || matchedSymbol.inputSchema)) {
        return { params: matchedSymbol.params, inputSchema: matchedSymbol.inputSchema };
    }

    return undefined;
}

export function getMissingRequiredTemplateInputNames(
    values: Record<string, unknown> | undefined,
    inputSchema: TemplateInputSchema | undefined,
    fallbackParams: readonly string[] | undefined,
): string[] {
    const required = getRequiredTemplateInputs(inputSchema, fallbackParams);
    if (required.length === 0) return [];

    const withDefaults = buildDefaultedTemplateInputs(values, inputSchema);
    return required.filter((name) => isMissingTemplateInputValue(withDefaults[name]));
}

export function getMissingRequiredTemplateInputsForUse(
    useBinding: UseBinding,
    {
        definitions,
        contentDefinitions,
        forDefinitions,
        uses,
        importSymbols,
    }: {
        definitions: Map<string, PluginDefinition>;
        contentDefinitions?: Map<string, ContentDefinition>;
        forDefinitions?: Map<string, ForDefinition>;
        uses?: readonly UseBinding[];
        importSymbols?: readonly ImportCachedSymbol[];
    },
): string[] {
    const source = resolveUseTemplateSource(
        useBinding.source,
        definitions,
        contentDefinitions,
        forDefinitions,
        uses,
        importSymbols,
    );
    if (!source) return [];
    return getMissingRequiredTemplateInputNames(
        useBinding.with,
        source.inputSchema,
        source.params,
    );
}

export function isUseBindingBlockedAsTemplate(
    useBinding: UseBinding | undefined,
    missingRequiredTemplateInputs: readonly string[],
): boolean {
    return !!useBinding && (
        useBinding.template === true ||
        missingRequiredTemplateInputs.length > 0
    );
}

export function isMissingTemplateInputValue(value: unknown): boolean {
    return value === undefined || value === null || value === "";
}
